import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import CardFeed from '../card/card-feed';
import CardCommunity from '../card/card-community';
import SkeletonListFeed from '../skeleton/SkeletonListFeed';
import PostCardSlider from '../home/PostCardSlider';
import { processPostsData } from '../../utils/postUtils';
import { useTranslation } from '../../i18n/useTranslation';
import authService from '../../services/authService';
import { fetchHomeFeedLikes, fetchPublicHomeFeed } from '../../services/homeFeedService';
import { readHomeFeedBootstrap } from '../../services/homeFeedBootstrap';

// 커뮤니티 피드 목록 중간(3번째, 13번째, 23번째...)에 삽입되는 pictures/cardnews 캐러셀 간격 설정
const MEDIA_CAROUSEL_START = 3;
const MEDIA_CAROUSEL_INTERVAL = 10;
const MEDIA_CAROUSEL_WINDOW = 5;

const EMPTY_PAGINATION = Object.freeze({
  currentPage: 1,
  pageSize: 10,
  totalPages: 0,
  totalPosts: 0,
});

function createInitialHomeFeedState(homeFeed) {
  const snapshot = homeFeed ? readHomeFeedBootstrap() : null;
  if (!snapshot) {
    return {
      fromBootstrap: false,
      posts: [],
      loading: true,
      hasMore: false,
      nextCursor: null,
      pagination: { ...EMPTY_PAGINATION },
    };
  }

  const posts = processPostsData(snapshot.data.posts).map((post) => ({
    ...post,
    hasLiked: false,
  }));
  const hasMore = Boolean(snapshot.data.hasMore);
  return {
    fromBootstrap: true,
    posts,
    loading: false,
    hasMore,
    nextCursor: snapshot.data.nextCursor || null,
    pagination: {
      currentPage: 1,
      pageSize: 10,
      totalPages: hasMore ? 2 : 1,
      totalPosts: posts.length,
    },
  };
}

function ListFeed({ filters, cardType = 'feed', showMediaCarousel = false, excludeMediaFormats = false, homeFeed = false }) {
  const { t } = useTranslation();
  const [initialHomeFeedState] = useState(() => createInitialHomeFeedState(homeFeed));
  const [posts, setPosts] = useState(() => initialHomeFeedState.posts);
  const [loading, setLoading] = useState(initialHomeFeedState.loading);
  const [hasMore, setHasMore] = useState(initialHomeFeedState.hasMore);
  const [pagination, setPagination] = useState(() => initialHomeFeedState.pagination);
  const [nextCursor, setNextCursor] = useState(initialHomeFeedState.nextCursor);
  const loaderRef = useRef(null);
  const paginationRef = useRef(pagination);
  const hasMoreRef = useRef(hasMore);
  const nextCursorRef = useRef(nextCursor);
  const loadingRef = useRef(false);
  const filtersRef = useRef(filters);
  const preserveBootstrapRef = useRef(initialHomeFeedState.fromBootstrap);
  const pageSize = pagination.pageSize;

  // ref 업데이트
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    nextCursorRef.current = nextCursor;
  }, [nextCursor]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // filters를 직렬화하여 변경 감지 (깊은 비교)
  const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);

  // 게시글 불러오는 함수 (페이지, 필터 인자 추가)
  const fetchPosts = useCallback(
    async (page = 1, currentFilters = {}, cursor = null) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        if (homeFeed) {
          const resData = await fetchPublicHomeFeed({ pageSize, cursor, kind: 'default' });
          if (!resData || typeof resData !== 'object') {
            console.error('Error fetching home feed: invalid response');
            return;
          }

          const processedPosts = processPostsData(resData.posts || []).map((post) => ({
            ...post,
            // 사용자별 상태가 도착하기 전에는 개별 카드별 /has_liked 요청을 만들지 않는다.
            hasLiked: false,
          }));
          setPosts((prev) => (cursor ? [...prev, ...processedPosts] : processedPosts));
          setHasMore(Boolean(resData.hasMore));
          setNextCursor(resData.nextCursor || null);
          setPagination((prev) => ({
            ...prev,
            currentPage: page,
            totalPages: resData.hasMore ? page + 1 : page,
            totalPosts: cursor ? prev.totalPosts + processedPosts.length : processedPosts.length,
          }));

          // 공개 목록을 먼저 렌더링하고, 로그인 사용자에게만 좋아요 상태를 비동기로 덧입힌다.
          if (authService.isLoggedIn() && processedPosts.length > 0) {
            fetchHomeFeedLikes(processedPosts.map((post) => post.id))
              .then((likes) => {
                setPosts((current) => current.map((post) => (
                  Object.prototype.hasOwnProperty.call(likes, post.id)
                    ? { ...post, hasLiked: Boolean(likes[post.id]) }
                    : post
                )));
              })
              .catch(() => {});
          }
          return;
        }

        // category가 'all' 또는 빈 값이면 쿼리에서 제거
        const params = { page, pageSize: pageSize, ...currentFilters };
        if (params.category === 'all' || !params.category) {
          delete params.category;
        }
        // pictures/cardnews는 별도 캐러셀에서만 노출하고 일반 목록에서는 제외
        if (excludeMediaFormats) {
          params.formats = 'default';
        }

        const response = await axios.get('/api/posts', { params });
        const resData = response.data?.data;
        if (!resData || typeof resData !== 'object') {
          console.error('Error fetching posts: invalid response (expected JSON with data). Is REACT_APP_API_URL set on Cloudflare Pages?', response.data);
          return;
        }
        setHasMore(Boolean(resData.hasMore));
        setPagination(resData.pagination ?? { currentPage: 1, pageSize, totalPages: 0, totalPosts: 0 });
        // featured_image가 없는 게시글은 콘텐츠에서 첫 번째 이미지 추출
        const processedPosts = processPostsData(resData.posts || []);
        setPosts((prev) => (page === 1 ? processedPosts : [...prev, ...processedPosts]));
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [pageSize, excludeMediaFormats, homeFeed]
  );

  // filters 변경 시 페이지 1부터 다시 요청
  useEffect(() => {
    const preserveBootstrap = homeFeed && preserveBootstrapRef.current;
    preserveBootstrapRef.current = false;
    if (!preserveBootstrap) {
      setPosts([]);
      setPagination({ ...EMPTY_PAGINATION });
      setNextCursor(null);
    }
    fetchPosts(1, filtersRef.current);
  }, [filtersKey, fetchPosts, homeFeed]);

  // Intersection Observer로 스크롤 감지
  useEffect(() => {
    if (!hasMore) return;

    const observer = new window.IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMoreRef.current) {
        const currentPage = paginationRef.current.currentPage;
        if (homeFeed) {
          const cursor = nextCursorRef.current;
          if (cursor) fetchPosts(currentPage + 1, filtersRef.current, cursor);
          return;
        }
        const totalPages = paginationRef.current.totalPages;
        if (currentPage < totalPages) {
          fetchPosts(currentPage + 1, filtersRef.current);
        }
      }
    });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, fetchPosts, homeFeed]);

  // pictures/cardnews 캐러셀용 게시글 별도 조회 (목록 중간 삽입)
  const [mediaPosts, setMediaPosts] = useState([]);
  useEffect(() => {
    if (!showMediaCarousel) return;
    const request = homeFeed
      ? fetchPublicHomeFeed({ pageSize: 30, kind: 'media' })
      : axios.get('/api/posts', {
          params: { status: 'published', formats: 'pictures,cardnews', pageSize: 30, sortColumn: 'timestamp', sortOrder: 'DESC' },
        }).then((res) => res.data?.data);
    request
      .then((res) => {
        const rows = Array.isArray(res?.posts) ? res.posts : [];
        setMediaPosts(processPostsData(rows));
      })
      .catch(() => setMediaPosts([]));
  }, [showMediaCarousel, homeFeed]);

  const getCarouselPosts = useCallback(
    (slotIndex) => {
      if (mediaPosts.length === 0) return [];
      const windowSize = Math.min(MEDIA_CAROUSEL_WINDOW, mediaPosts.length);
      const start = (slotIndex * MEDIA_CAROUSEL_WINDOW) % mediaPosts.length;
      const picked = [];
      for (let i = 0; i < windowSize; i++) {
        picked.push(mediaPosts[(start + i) % mediaPosts.length]);
      }
      return picked;
    },
    [mediaPosts]
  );

  const priorityImagePostIndex = homeFeed
    ? posts.findIndex((post) => Boolean(post.featured_image || post.first_image_url))
    : -1;

  return (
    <div className='box-col pd0 gap10'>
      {posts.length === 0 && (
        loading ? (
          <SkeletonListFeed count={3} cardType={cardType} />
        ) : (
          <div className='group-align-center'>{t('feed.noMorePast')}</div>
        )
      )}
      {posts.map((post, index) => {
        const displayIndex = index + 1; // 1-based
        const isCarouselSlot =
          showMediaCarousel &&
          displayIndex >= MEDIA_CAROUSEL_START &&
          (displayIndex - MEDIA_CAROUSEL_START) % MEDIA_CAROUSEL_INTERVAL === 0;
        const slidePosts = isCarouselSlot
          ? getCarouselPosts((displayIndex - MEDIA_CAROUSEL_START) / MEDIA_CAROUSEL_INTERVAL)
          : null;
        return (
          <React.Fragment key={post.id}>
            {cardType === 'community' ?
              <CardCommunity {...post} /> :
              <CardFeed
                {...post}
                imageFetchPriority={index === priorityImagePostIndex ? 'high' : undefined}
              />}
            {slidePosts && slidePosts.length > 0 && <PostCardSlider posts={slidePosts} />}
          </React.Fragment>
        );
      })}
      {/* 스크롤 끝 마커 */}
      <div ref={loaderRef} style={{ height: 20 }} />
      {hasMore && <div className='group-align-center'>{t('feed.loadingMore')}</div>}
    </div>
  );
}

export default ListFeed;
